/**
 * D295: what a perk, an Almanac upgrade or a trait adds up to, now and after the next level, so
 * the screen can show the total and not only the step. Every figure comes from the function that
 * applies it to the world, so the screen cannot disagree with the numbers.
 */
import type { IdleContent } from '@hedgerow/data';
import { idleEffects, idleFactor, idlePerkFactor, idleTraitGrowthCut } from './effects.ts';
import { idlePerkFestivals } from './world.ts';
import { IDLE_HOUR_MS, idleBarnBaseMs } from './rules.ts';
import {
  idleKeepShare,
  idlePerkLevel,
  idlePerkStartCoins,
  idleTraitBarnLevel,
  idleTraitLevel,
  idleTraitStartCount,
} from './festival.ts';
import type { IdleWorldState, IdleStat } from './state.ts';

/**
 * A total now and after the next level; `next` is null at a top level.
 *   factor      a multiplier on `stat` (below 1 is cheaper)
 *   coins       coins every year starts with
 *   festivals   festivals counted towards opening producers
 *   crops       producers every year starts with, run by hands
 *   barn_hours  hours the barn a generation starts with holds
 *   growth_cut  how much lower every producer's cost growth is
 *   share       the share of the last generation's Heritage kept, 0 to 1
 */
export type IdleTotal =
  | { kind: 'factor'; stat: IdleStat; now: number; next: number | null }
  | {
      kind: 'coins' | 'festivals' | 'crops' | 'barn_hours' | 'growth_cut' | 'share';
      now: number;
      next: number | null;
    };

/** A festival perk's total. Null for a perk with nothing to add up: an unlock or a head start. */
export function idlePerkTotal(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
): IdleTotal | null {
  const perk = content.perks.find((k) => k.id === id);
  if (!perk) return null;
  const level = idlePerkLevel(state, id);
  const top = perk.maxLevel !== undefined && level >= perk.maxLevel;
  const at = (f: (l: number) => number) => ({ now: f(level), next: top ? null : f(level + 1) });
  if (perk.effect)
    return { kind: 'factor', stat: perk.effect.stat, ...at((l) => idlePerkFactor(perk, l)) };
  if (perk.startCoins) return { kind: 'coins', ...at((l) => idlePerkStartCoins(perk, l)) };
  if (perk.festivals) return { kind: 'festivals', ...at((l) => idlePerkFestivals(perk, l)) };
  return null;
}

/**
 * An Almanac upgrade's total: what this year's upgrades give the stat it changes, for the
 * producer it names (or every crop), now and once it is bought. Read through `idleEffects`, so
 * a synergy counts as the world stands.
 */
export function idleUpgradeTotal(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
): IdleTotal | null {
  const u = content.upgrades.find((x) => x.id === id);
  if (!u) return null;
  const total = (s: IdleWorldState) =>
    idleFactor(
      idleEffects(s, content, s.advancedTo).filter((e) => e.source.startsWith('upgrade:')),
      u.effect.stat,
      u.effect.producer,
    );
  const bought = state.year.upgrades.includes(id);
  const after = { ...state, year: { ...state.year, upgrades: [...state.year.upgrades, id] } };
  return {
    kind: 'factor',
    stat: u.effect.stat,
    now: total(state),
    next: bought ? null : total(after),
  };
}

/** A Lineage trait's total. Null for a trait with nothing to add up (hands hired). */
export function idleTraitTotal(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
): IdleTotal | null {
  const t = content.lineage.traits.find((x) => x.id === id);
  if (!t) return null;
  const level = idleTraitLevel(state, id);
  const top = t.maxLevel !== undefined && level >= t.maxLevel;
  const at = (f: (l: number) => number) => ({ now: f(level), next: top ? null : f(level + 1) });
  if (t.startProducers?.length) return { kind: 'crops', ...at((l) => idleTraitStartCount(t, l)) };
  if (t.startBarnLevel)
    return {
      kind: 'barn_hours',
      ...at((l) =>
        l > 0 ? idleBarnBaseMs(content, idleTraitBarnLevel(content, t, l)) / IDLE_HOUR_MS : 0,
      ),
    };
  if (t.costGrowthCut) return { kind: 'growth_cut', ...at((l) => idleTraitGrowthCut(t, l)) };
  if (t.keepHeritage) return { kind: 'share', ...at((l) => idleKeepShare(t.keepHeritage!, l)) };
  return null;
}
